import { useCallback, useEffect, useMemo, useState } from "react";
import { storyblokEditable } from "@storyblok/react";
import { Link } from "react-router-dom";
import { sbImage } from "../utils/storyblokImage";

// Resolve links for buttons
const resolveStoryblokLink = (link) => {
  if (!link) return "";

  if (link.linktype === "story") {
    const slug = link.cached_url || link.story?.full_slug;
    return slug ? `/${slug}` : "";
  }

  if (link.linktype === "asset") return link.url || "";

  return link.url || link.cached_url || "";
};

const isInternalPath = (href) => {
  if (!href || typeof href !== "string") return false;
  const safeHref = href.trim();
  return safeHref.startsWith("/") && !safeHref.startsWith("//");
};

const SmartLink = ({ href, className, children, ...rest }) => {
  const safeHref = typeof href === "string" ? href.trim() : "";

  if (!safeHref) {
    return (
      <button type="button" className={className} {...rest}>
        {children}
      </button>
    );
  }

  if (isInternalPath(safeHref)) {
    return (
      <Link to={safeHref} className={className} {...rest}>
        {children}
      </Link>
    );
  }

  return (
    <a
      href={safeHref}
      className={className}
      rel={rest.target === "_blank" ? "noreferrer noopener" : undefined}
      {...rest}
    >
      {children}
    </a>
  );
};

const buildImageCandidates = (url, { w, h, q } = {}) => {
  if (!url) return [];
  const candidates = [
    sbImage(url, { w, h, q, fmt: "webp" }),
    sbImage(url, { w, h, q, fmt: "auto" }),
    url,
  ];
  return Array.from(new Set(candidates.filter(Boolean)));
};

const ContactUsHero = ({ blok }) => {
  // Background image
  const bgUrlRaw = blok?.background_image?.filename || "";
  const bgAlt = blok?.background_image?.alt || "Background image";

  const bgCandidates = useMemo(
    () => buildImageCandidates(bgUrlRaw, { w: 1934, h: 1080, q: 80 }),
    [bgUrlRaw]
  );

  const [bgIndex, setBgIndex] = useState(0);
  const [isBgReady, setIsBgReady] = useState(false);

  useEffect(() => {
    setBgIndex(0);
    setIsBgReady(false);
  }, [bgUrlRaw]);

  const bgSrc = bgCandidates[bgIndex] || bgUrlRaw;

  const handleBgError = useCallback(() => {
    setIsBgReady(false);
    setBgIndex((i) => {
      const next = i + 1;
      return next < bgCandidates.length ? next : i;
    });
  }, [bgCandidates.length]);

  // Preload bgSrc
  useEffect(() => {
    if (!bgSrc) {
      setIsBgReady(true);
      return;
    }

    let cancelled = false;
    setIsBgReady(false);

    const img = new Image();
    img.decoding = "async";
    img.fetchPriority = "high";
    img.src = bgSrc;

    img.onload = () => {
      if (!cancelled) setIsBgReady(true);
    };

    img.onerror = () => {
      if (cancelled) return;
      handleBgError();
    };

    return () => {
      cancelled = true;
    };
  }, [bgSrc, handleBgError]);

  // overlay if needed
  const overlayOpacity = useMemo(() => {
    const n = Number(blok?.overlay_opacity ?? 0.45);
    if (Number.isNaN(n)) return 0.45;
    return Math.max(0, Math.min(0.85, n));
  }, [blok?.overlay_opacity]);

  // Buttons
  const primaryLabel = blok?.primary_button || "";
  const primary = primaryLabel
    ? {
        label: primaryLabel,
        href: resolveStoryblokLink(blok?.primary_button_link),
        icon: blok?.primary_icon?.filename || "",
        iconAlt: blok?.primary_icon?.alt || "",
      }
    : null;

  const secondaryLabel = blok?.secondary_button || "";
  const secondary = secondaryLabel
    ? {
        label: secondaryLabel,
        href: resolveStoryblokLink(blok?.secondary_button_link),
        icon: blok?.secondary_icon?.filename || "",
        iconAlt: blok?.secondary_icon?.alt || "",
      }
    : null;

  const primaryIconSrc = primary?.icon
    ? sbImage(primary.icon, { w: 40, h: 40, q: 90, fmt: "auto" })
    : "";

  const secondaryIconSrc = secondary?.icon
    ? sbImage(secondary.icon, { w: 40, h: 40, q: 90, fmt: "auto" })
    : "";

  // styling
  const btnBase =
    "inline-flex items-center justify-center gap-3 rounded-full px-6 py-3 text-sm md:text-base font-medium transition focus:outline-none focus:ring-2 focus:ring-white/70";

  const btnBlue = "bg-blue-600 text-white hover:bg-blue-700";

  return (
    <section
      {...storyblokEditable(blok)}
      className="relative w-full overflow-hidden bg-black font-['Roboto']"
    >
      {/* bg */}
      {bgSrc && (
        <div
          className={`absolute inset-0 transition-all duration-500 ${
            isBgReady
              ? "blur-0 scale-100 opacity-100"
              : "blur-sm scale-[1.01] opacity-85"
          }`}
        >
          <img
            key={`bg-${bgSrc}`}
            src={bgSrc}
            alt={bgAlt}
            className="absolute inset-0 h-full w-full object-cover"
            decoding="async"
            fetchpriority="high"
            onError={handleBgError}
          />
        </div>
      )}

      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black"
        style={{ opacity: overlayOpacity }}
        aria-hidden="true"
      />

      {/* Content always visible immediately */}
      <div className="relative z-10 mx-auto flex min-h-[520px] max-w-6xl items-center px-6 py-16">
        <div className="w-full text-center text-white">
          {blok?.title && (
            <h1 className="mx-auto max-w-4xl font-medium leading-tight text-[40px] md:text-[60px]">
              {blok.title}
            </h1>
          )}

          {blok?.subtitle && (
            <p className="mx-auto mt-5 max-w-3xl font-medium text-[16px] md:text-[20px] text-white/90">
              {blok.subtitle}
            </p>
          )}

          {(primary || secondary) && (
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              {primary && (
                <SmartLink
                  href={primary.href}
                  aria-label={primary.label}
                  className={`${btnBase} ${btnBlue}`}
                >
                  {primaryIconSrc && (
                    <img
                      src={primaryIconSrc}
                      alt={primary.iconAlt || ""}
                      aria-hidden={primary.iconAlt ? undefined : true}
                      className="h-5 w-5 shrink-0"
                      decoding="async"
                      loading="lazy"
                    />
                  )}
                  <span className="whitespace-nowrap">{primary.label}</span>
                </SmartLink>
              )}

              {secondary && (
                <SmartLink
                  href={secondary.href}
                  aria-label={secondary.label}
                  className={`${btnBase} ${btnBlue}`}
                >
                  {secondaryIconSrc && (
                    <img
                      src={secondaryIconSrc}
                      alt={secondary.iconAlt || ""}
                      aria-hidden={secondary.iconAlt ? undefined : true}
                      className="h-5 w-5 shrink-0"
                      decoding="async"
                      loading="lazy"
                    />
                  )}
                  <span className="whitespace-nowrap">{secondary.label}</span>
                </SmartLink>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ContactUsHero;